import { useNavigate, Link } from "react-router-dom";
import { useState, useContext, useEffect, useRef } from "react";
import { getAuth } from "firebase/auth";
import toast, { Toaster } from "react-hot-toast";
import { firebaseApp } from "../db/Firebase";
import { getFirestore } from "firebase/firestore";
import UserContext from "./UserContext";
import { setPersistence, browserSessionPersistence } from "firebase/auth";
import { FaEyeSlash, FaEye } from "react-icons/fa6";

import "./SignInnew.css";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export function SignInnew({ open }) {
  const [signInMethod, setSignInMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeInput, setActiveInput] = useState(null);

  // Context
  const {
    user,
    authError,
    clearAuthError,
    signInWithEmail,
    signInWithGoogle,
    createRecaptchaVerifier,
    sendPhoneVerificationCode,
    isRateLimited,
  } = useContext(UserContext);

  const navigate = useNavigate();

  // Refs
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifier = useRef(null);
  const formRef = useRef(null);

  // Navigate if already logged in
  useEffect(() => {
    if (user) {
      navigate("/Home");
    }
  }, [user, navigate]);

  // Email sign in handler

  useEffect(() => {
    clearAuthError();
    setShowVerificationInput(false);
  }, [signInMethod, clearAuthError]);

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifier.current) {
        try {
          recaptchaVerifier.current.clear();
          recaptchaVerifier.current = null;
        } catch (error) {
          console.error("Error clearing recaptcha:", error);
        }
      }
    };
  }, []);

  // Animation for background bubbles
  useEffect(() => {
    const createBubble = () => {
      const bubbleContainer = document.querySelector(".background-animation");
      if (!bubbleContainer) return;

      const bubble = document.createElement("div");
      bubble.classList.add("bubble");

      // Random size between 10px and 50px
      const size = Math.random() * 40 + 10;
      bubble.style.width = `${size}px`;
      bubble.style.height = `${size}px`;

      // Random position
      bubble.style.left = `${Math.random() * 100}%`;

      // Random opacity
      bubble.style.opacity = `${Math.random() * 0.3 + 0.1}`;

      // Random animation duration
      const duration = Math.random() * 10 + 10;
      bubble.style.animationDuration = `${duration}s`;

      bubbleContainer.appendChild(bubble);

      // Remove bubble after animation completes
      setTimeout(() => {
        bubble.remove();
      }, duration * 1000);
    };

    // Create bubbles at intervals
    const interval = setInterval(createBubble, 1000);

    // Initial bubbles
    for (let i = 0; i < 10; i++) {
      createBubble();
    }

    return () => clearInterval(interval);
  }, []);

  const handleInputFocus = (name) => {
    setActiveInput(name);
  };

  const handleInputBlur = () => {
    setActiveInput(null);
  };

  const startPhoneVerification = async () => {
    // Validate phone number
    if (!phoneNumber) {
      toast.error("Please enter your phone number");
      return;
    }

    // Format phone number
    const formattedPhoneNumber = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+${phoneNumber}`;

    setLoading(true);
    try {
      // Initialize recaptcha if not already initialized
      if (!recaptchaVerifier.current) {
        recaptchaVerifier.current = createRecaptchaVerifier(
          recaptchaContainerRef.current
        );

        if (!recaptchaVerifier.current) {
          throw new Error(
            "Failed to initialize verification. Please refresh and try again."
          );
        }
      }

      // Send verification code
      const confirmation = await sendPhoneVerificationCode(
        formattedPhoneNumber,
        recaptchaVerifier.current
      );

      setConfirmationResult(confirmation);
      setShowVerificationInput(true);
      toast.success("Verification code sent!");

      // Set code expiration timer (5 minutes)
      setTimeout(() => {
        if (showVerificationInput) {
          setShowVerificationInput(false);
          toast.error("Verification code expired. Please request a new one.");
        }
      }, 5 * 60 * 1000);
    } catch (error) {
      toast.error(error.message);

      // Reset recaptcha on error
      if (recaptchaVerifier.current) {
        try {
          recaptchaVerifier.current.clear();
          recaptchaVerifier.current = null;
        } catch (error) {
          console.error("Error clearing recaptcha:", error);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (!verificationCode) {
      toast.error("Please enter the verification code");
      return;
    }

    setLoading(true);
    try {
      await confirmationResult.confirm(verificationCode);
      toast.success("Phone verification successful!");
      // User is automatically set in context via Firebase auth state change
    } catch (error) {
      toast.error("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (e) => {
    e?.preventDefault();

    // Form validation
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmail(email, password);
      toast.success("Sign in successful!");
      // Navigation is handled by the useEffect watching the user state
    } catch (error) {
      // Error is already set in context
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithGoogle();
      toast.success("Sign in with Google successful!");
      // Navigation is handled by the useEffect watching the user state
    } catch (error) {
      // Error is already set in context
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      signIn();
    }
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="signin-element">
        <div className="background-animation"></div>
        <div className="signin-content">
          <div className="signin-container">
            <div className="signin-form-wrapper" ref={formRef}>
              <div className="signin-header">
                <h1 className="signin-title">Welcome Back</h1>
                {authError && <div className="error-message">{authError}</div>}
              </div>

              <div className="signin-method-toggle">
                <button
                  onClick={() => {
                    setSignInMethod("email");
                    setShowVerificationInput(false);
                  }}
                  className={signInMethod === "email" ? "active" : ""}
                >
                  Email
                </button>
                <button
                  onClick={() => {
                    setSignInMethod("phone");
                    setShowVerificationInput(false);
                  }}
                  className={signInMethod === "phone" ? "active" : ""}
                >
                  Phone
                </button>
              </div>

              <div className="form-fields">
                {signInMethod === "email" ? (
                  <form onKeyDown={handleKeyPress} onSubmit={signIn}>
                    <div
                      className={`input-field ${
                        activeInput === "email" ? "active" : ""
                      }`}
                    >
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input"
                        disabled={loading || isRateLimited}
                        onFocus={() => handleInputFocus("email")}
                        onBlur={handleInputBlur}
                      />
                      <label htmlFor="email" className={email ? "filled" : ""}>
                        Email
                      </label>
                    </div>

                    <div
                      className={`input-field ${
                        activeInput === "password" ? "active" : ""
                      }`}
                    >
                      <div className="password-input-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="input"
                          disabled={loading || isRateLimited}
                          onFocus={() => handleInputFocus("password")}
                          onBlur={handleInputBlur}
                        />
                        <label
                          htmlFor="password"
                          className={password ? "filled" : ""}
                        >
                          Password
                        </label>
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEye /> : <FaEyeSlash />}
                        </button>
                      </div>
                    </div>

                    <div className="forgot-password">
                      <Link to="/forgot-password">Forgot password?</Link>
                    </div>

                    <div className="button-field">
                      <button
                        onClick={signIn}
                        className="btn primary-btn"
                        disabled={loading || isRateLimited}
                      >
                        {loading ? "Signing in..." : "Sign In"}
                      </button>
                    </div>
                    <div className="button-field">
                      <button
                        onClick={handleGoogleSignIn}
                        className="btn google-btn"
                        disabled={loading || isRateLimited}
                        type="button"
                      >
                        {loading ? "Signing in..." : "Sign In with Google"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div
                      ref={recaptchaContainerRef}
                      id="recaptcha-container"
                    ></div>

                    {!showVerificationInput ? (
                      <div
                        className={`input-field ${
                          activeInput === "phoneNumber" ? "active" : ""
                        }`}
                      >
                        <input
                          type="tel"
                          id="phoneNumber"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          className="input"
                          disabled={loading || isRateLimited}
                          onFocus={() => handleInputFocus("phoneNumber")}
                          onBlur={handleInputBlur}
                        />
                        <label
                          htmlFor="phoneNumber"
                          className={phoneNumber ? "filled" : ""}
                        >
                          Phone Number
                        </label>

                        <div className="button-field">
                          <button
                            onClick={startPhoneVerification}
                            className="btn primary-btn"
                            disabled={loading || isRateLimited}
                          >
                            {loading
                              ? "Sending Code..."
                              : "Get Verification Code"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          className={`input-field ${
                            activeInput === "verificationCode" ? "active" : ""
                          }`}
                        >
                          <input
                            type="text"
                            id="verificationCode"
                            value={verificationCode}
                            onChange={(e) =>
                              setVerificationCode(e.target.value)
                            }
                            className="input"
                            disabled={loading || isRateLimited}
                            onFocus={() => handleInputFocus("verificationCode")}
                            onBlur={handleInputBlur}
                          />
                          <label
                            htmlFor="verificationCode"
                            className={verificationCode ? "filled" : ""}
                          >
                            Verification Code
                          </label>
                        </div>
                        <div className="button-field">
                          <button
                            onClick={verifyPhoneCode}
                            className="btn primary-btn"
                            disabled={loading || isRateLimited}
                          >
                            {loading ? "Verifying..." : "Sign In"}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}

                <div className="signup-link">
                  <p>
                    Don't have an account? <Link to={"/SignUp"}>Sign Up</Link>
                  </p>
                </div>

                {isRateLimited && (
                  <p className="rate-limit-message">
                    Too many requests. Please wait before trying again.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
